// src/padplanner/ChecksTab.jsx
//
// The editor's Checks tab and its Layout checks panel (S5b, mockup 4). What
// they show is the server's run (checksVocabulary.js names its verdict); the
// rows are grouped by rule. Clicking a failing row hands it to the page,
// which selects and frames the features it names.
import { useState } from "react";
import { SectionLabel } from "./SidePanel";
import {
  verdictOf, groupByRule, resultText, witnessText, VERDICT_HEADLINES, VERDICT_TONES,
  NOT_CHECKED_TEXT, STALE_TEXT, NO_RUN_TEXT, NO_SUBJECTS_TEXT, RERUN_TEXT,
} from "./checksVocabulary";
import { SEVERITY_LABELS, relationText } from "./ruleVocabulary";
import { refusalText } from "./planRefusals";

// A rule's failing rows shown before "Show all": 1,460 rows at once is a
// list nobody reads and a render nobody waits for.
export const ROWS_PER_RULE = 25;
const NO_RERUN = Object.freeze({ busy: false, error: null });

const sevChip = (s) => `op-chip px-1.5 py-0 ${s === "block" ? "border-accent-red/60 text-accent-red" : "text-[rgb(var(--warn-text))]"}`;

function Verdict({ checks, onRerun, rerun = NO_RERUN }) {
  const verdict = verdictOf(checks);
  const run = checks.run;
  return (
    <div className="flex flex-col gap-1.5 rounded border border-bg-border bg-bg px-3 py-2.5" data-testid="pp-checks-verdict" data-verdict={verdict}>
      <p className={`text-[13.5px] font-semibold ${VERDICT_TONES[verdict]}`}>{VERDICT_HEADLINES[verdict]}</p>
      {verdict !== "not_checked" && run && (
        <p className="op-meta" data-testid="pp-checks-counts">
          {run.counts.block_fail} block · {run.counts.warn_fail} warn · {run.counts.pass} pass
          {run.counts.no_subjects ? ` · ${run.counts.no_subjects} matched nothing` : ""}
        </p>
      )}
      {verdict !== "not_checked" && run?.rule_set && (
        <p className="op-meta">Checked against {run.rule_set.name ?? "the rules"} v{run.rule_set.version}</p>
      )}
      {verdict === "stale" && <p className="op-meta">{run ? STALE_TEXT : NO_RUN_TEXT}</p>}
      {/* Only where the user may run them (EDIT, the current revision): otherwise no onRerun, no button. */}
      <RerunChecks onRerun={verdict === "stale" ? onRerun : null} rerun={rerun} />
    </div>
  );
}

/**
 * Re-run checks (POST …/revisions/current/checks) and its refusal, in the
 * Checks tab's words; Submit offers the same button when the checks are
 * out of date (PlanReview.jsx SubmitButton).
 */
export function RerunChecks({ onRerun, rerun = NO_RERUN, testid = undefined }) {
  return (
    <>
      {onRerun && (
        <button type="button" className="op-btn op-btn--primary op-btn--sm self-start" onClick={onRerun} disabled={rerun.busy} data-testid={testid}>
          {rerun.busy ? "Checking…" : RERUN_TEXT}
        </button>
      )}
      {rerun.error && (
        <p className="text-[12.5px] text-accent-red" role="alert">{refusalText(rerun.error)} <span className="op-mono">({rerun.error.code ?? rerun.error.status})</span></p>
      )}
    </>
  );
}

function RuleGroup({ g, onPick }) {
  const [all, setAll] = useState(false);
  const rows = all ? g.fails : g.fails.slice(0, ROWS_PER_RULE);
  return (
    <section className="flex flex-col gap-1" data-testid="pp-checks-rule" data-rule-key={g.rule_key}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-[13px] text-fg-primary">{g.message ?? g.rule_key}</p>
        <span className={sevChip(g.severity)}>{SEVERITY_LABELS[g.severity] ?? g.severity}</span>
      </div>
      <p className="op-meta">
        <span className="op-mono">{g.rule_key}</span> · {relationText(g)}
        {g.fails.length ? ` · ${g.fails.length} failing` : ""}{g.passes ? ` · ${g.passes} pass` : ""}
      </p>
      {g.noSubjects && <p className="op-meta italic" data-testid="pp-checks-no-subjects">{NO_SUBJECTS_TEXT}</p>}
      {rows.length > 0 && (
        <ul className="flex flex-col gap-0.5">
          {rows.map((r, i) => (
            <li key={`${r.subject?.feature_id ?? "s"}-${r.object?.feature_id ?? "o"}-${i}`}>
              <button type="button" data-testid="pp-checks-row" onClick={() => onPick(r)}
                className="flex w-full items-center justify-between gap-2 rounded px-1.5 py-1 text-left text-[12.5px] text-fg-primary hover:bg-bg-soft">
                <span className="min-w-0 truncate" title={resultText(r)}>{resultText(r)}</span>
                {witnessText(r) && <span className="op-mono shrink-0 text-accent-red">{witnessText(r)}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      {!all && g.fails.length > ROWS_PER_RULE && (
        <button type="button" className="op-meta self-start hover:text-fg-primary" onClick={() => setAll(true)}>
          Show all {g.fails.length}
        </button>
      )}
    </section>
  );
}

/**
 * @param {object} p
 * @param {object} p.checks   {run, rules, results, full}: full is true when results are every outcome (GET …/checks)
 * @param {Map} p.rulesById
 * @param {() => void} p.onRerun
 * @param {{busy: boolean, error: object|null}} p.rerun
 * @param {(result: object) => void} p.onPick
 */
export default function ChecksTab({ checks, rulesById, onRerun, rerun, onPick }) {
  if (!checks.loaded) return <p className="op-meta">Loading checks…</p>;
  if (checks.error) return <p className="text-sm text-accent-red" role="alert">Couldn't load the checks: {checks.error}</p>;
  const verdict = verdictOf(checks);
  const groups = verdict === "not_checked" ? [] : groupByRule(checks.results, rulesById);
  return (
    <div className="flex flex-col gap-3" data-testid="pp-checks">
      <Verdict checks={checks} onRerun={onRerun} rerun={rerun} />
      {verdict !== "not_checked" && !checks.full && <p className="op-meta">Showing what needs attention; passing results are loading…</p>}
      {groups.map((g) => <RuleGroup key={g.rule_id} g={g} onPick={onPick} />)}
      {verdict !== "not_checked" && checks.full && groups.length === 0 && checks.run && <p className="op-meta">No results.</p>}
      <p className="op-meta border-t border-bg-border pt-2">Drafts are checked against the organization's published rules. Click a row to show it on the map.</p>
    </div>
  );
}

/**
 * The map's Layout checks panel: a line per rule, red when it fails (mockup
 * 4), in a MapHud slot (bottomLeft). The measured value and the limit come
 * first ("75.4′ · min 100′"), then the rule's message wrapped to at most two
 * lines; the whole line is the row's tooltip, and the Checks list in the
 * panel has every message in full. So what is cut, if anything, is the end
 * of the message, never the figures.
 */
export const LAYOUT_CHECKS_FULL_TEXT = "Hover a rule for all of it; the Checks list in the panel has every message in full.";

export function LayoutChecksPanel({ checks, rulesById }) {
  const verdict = verdictOf(checks);
  const groups = checks.loaded && verdict !== "not_checked" ? groupByRule(checks.results, rulesById) : [];
  return (
    <div className="w-[260px] max-w-full rounded-md border border-bg-border bg-bg/90 px-3 py-2 shadow" data-testid="pp-layout-checks">
      <div className="op-label uppercase">Layout checks</div>
      {!checks.loaded && <p className="op-meta">Loading…</p>}
      {checks.loaded && verdict === "not_checked" && <p className="op-meta" data-testid="pp-layout-not-checked">{NOT_CHECKED_TEXT}</p>}
      {checks.loaded && verdict === "stale" && <p className="op-meta">{VERDICT_HEADLINES.stale}</p>}
      <ul className="flex flex-col gap-1">
        {groups.slice(0, 8).map((g) => {
          const fail = g.fails[0];
          const tone = g.fails.length ? (g.severity === "block" ? "bg-accent-red" : "bg-[rgb(var(--warn))]") : g.noSubjects ? "bg-fg-secondary" : "bg-status-ok";
          const witness = fail ? witnessText(fail) : null;
          const message = `${g.message ?? g.rule_key}${!fail && g.noSubjects ? ` — ${NO_SUBJECTS_TEXT}` : ""}`;
          return (
            <li key={g.rule_id} className="flex items-start gap-2 text-[12.5px] text-fg-primary" data-testid="pp-layout-check"
              title={witness ? `${witness} — ${message}` : message}>
              <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${tone}`} aria-hidden="true" />
              <span className="flex min-w-0 flex-col">
                {witness && <span className="op-mono text-[11.5px] text-fg-primary" data-testid="pp-layout-check-witness">{witness}</span>}
                <span className="line-clamp-2 break-words" data-testid="pp-layout-check-message">{message}</span>
              </span>
            </li>
          );
        })}
      </ul>
      {groups.length > 0 && <p className="op-meta mt-1.5 text-[11px]" data-testid="pp-layout-checks-full">{LAYOUT_CHECKS_FULL_TEXT}</p>}
    </div>
  );
}
